import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Database, Wifi, WifiOff, AlertTriangle } from 'lucide-react';

// Hook for demo mode state management (shared via custom events)
const STORAGE_KEY = 'ybv_demo_mode';
const SYNC_EVENT = 'ybv_demo_mode_change';

export function useDemoMode() {
  const [isDemoMode, setIsDemoMode] = React.useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : true;
  });

  React.useEffect(() => {
    const handler = (e) => setIsDemoMode(e.detail);
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  const toggleDemoMode = React.useCallback((value) => {
    setIsDemoMode(value);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: value }));
  }, []);

  return { isDemoMode, toggleDemoMode };
}

// Settings-panel variant (enterprise-safe wording)
export function DatasetModeControl({ isDemoMode, onToggle }) {
  return (
    <div className="space-y-3" data-testid="dataset-mode-control">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground">Dataset Mode</label>
          <p className="text-xs text-muted-foreground">
            {isDemoMode ? 'Pilot dataset (simulated)' : 'Production baseline (default)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={!isDemoMode}
            onCheckedChange={(checked) => onToggle(!checked)}
            data-testid="dataset-mode-toggle"
          />
          <Badge
            variant="outline"
            className={isDemoMode
              ? 'border-warning/30 text-warning bg-warning/10 text-xs'
              : 'border-stable/30 text-stable bg-stable/10 text-xs'
            }
          >
            {isDemoMode ? 'Pilot' : 'Production'}
          </Badge>
        </div>
      </div>
      <p className="text-2xs text-muted-foreground/60">
        Internal testing setting. Do not change for stakeholder demos.
      </p>
    </div>
  );
}

export default function DemoModeToggle({ isDemoMode, onToggle, className = '' }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2">
        {isDemoMode ? (
          <WifiOff className="w-4 h-4 text-warning" />
        ) : (
          <Wifi className="w-4 h-4 text-stable" />
        )}
        <span className="text-sm text-muted-foreground">Data Mode:</span>
      </div>
      
      <div className="flex items-center gap-2">
        <Switch 
          checked={!isDemoMode}
          onCheckedChange={(checked) => onToggle(!checked)}
          data-testid="demo-mode-toggle"
        />
        <Badge 
          variant="outline" 
          className={isDemoMode 
            ? 'border-warning/30 text-warning bg-warning/10' 
            : 'border-stable/30 text-stable bg-stable/10'
          }
        >
          {isDemoMode ? (
            <>
              <Database className="w-3 h-3 mr-1" />
              Demo
            </>
          ) : (
            <>
              <Wifi className="w-3 h-3 mr-1" />
              Live
            </>
          )}
        </Badge>
      </div>
      
      {isDemoMode && (
        <div className="flex items-center gap-1 text-xs text-warning">
          <AlertTriangle className="w-3 h-3" />
          <span>Using simulated data</span>
        </div>
      )}
    </div>
  );
}
